// ============================================
// TYPES & CONSTANTS for StageFlow
// Data now lives in Supabase via DataContext.
// This file retains type definitions, static
// constants, and pure computation helpers.
// ============================================

// CalendarKind is defined canonically in the import-free calendars.ts (which
// CI type-checks in isolation); re-exported here so callers importing domain
// types from mockData find it alongside the Calendar record type.
import type { CalendarKind } from './calendars';
export type { CalendarKind } from './calendars';
export { getDeliverableOwner, isOwnedByUser, withDeliverableOwner } from './deliverableOwner';
import { summarizeCompletion } from './deliverableArchive';
import type { ArchiveReason } from './deliverableArchive';
export type { ArchiveReason } from './deliverableArchive';
export { isAbandoned } from './deliverableArchive';

export type ProjectStatus = 'later-start-date' | 'planning' | 'in-progress' | 'complete' | 'delayed' | 'cancelled' | 'ongoing';
export const TERMINAL_PROJECT_STATUSES: ReadonlySet<ProjectStatus> = new Set(['complete', 'cancelled']);
export const INACTIVE_PROJECT_STATUSES: ReadonlySet<ProjectStatus> = new Set(['complete', 'cancelled', 'later-start-date']);
export type DeliverableStatus = 'not-started' | 'in-progress' | 'review' | 'approved' | 'complete';
export type EnergyLevel = 'quick-win' | 'medium-effort' | 'deep-focus';
// Department is stored as its slug string. The known set below is the seed
// list; admins can add/edit/archive departments at /settings/departments, so
// runtime values are widened to `string`.
export type KnownDepartment =
  | 'Marketing' | 'Facility' | 'Scenic' | 'Costumes' | 'Props' | 'Tech' | 'Website' | 'Media'
  | 'Creative' | 'Stage Management' | 'Design' | 'Production Leadership'
  | 'Electrics' | 'Sound' | 'Show Control' | 'Video'
  | 'Rigging' | 'Special Effects' | 'Wardrobe' | 'Hair & Makeup'
  | 'Music' | 'Production Support' | 'Education' | 'Volunteer';
export type Department = KnownDepartment | (string & {});
export type ProposalStatus = 'proposed' | 'confirmed' | 'cancelled';
export type CustomerStatus = 'new-inquiry' | 'quoting' | 'in-production' | 'delivered' | 'invoiced' | 'archived';
export type TeamRole = 'admin' | 'member';

// ============================================
// PROJECT TEMPLATES
// ============================================

export interface DeliverableBlueprint {
  id: string;
  name: string;
  department: Department;
  energyLevel: EnergyLevel;
  type?: string;
  task: string;
  estimatedMinutes?: number;
  relativeDueDays: number;       // days relative to anchor (negative = before anchor, positive = after)
  relativeDueAnchor: 'opening' | 'kickoff'; // which project date to calculate from
  isClientWork?: boolean;
  workingTeam?: string[];
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  blueprints: DeliverableBlueprint[];
  createdAt: string;
  updatedAt: string;
}

// ============================================
// HELP WIKI
// ============================================

export type HelpArticleType = 'article' | 'setup-guide' | 'glossary';

export interface HelpSetupStep {
  id: string;
  title: string;
  body: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  type: HelpArticleType;
  category: string;
  tags: string[];
  body: string;
  relatedSlugs?: string[];
  steps?: HelpSetupStep[];
  order: number;
  isPinned?: boolean;
  updatedAt: string;
  createdAt: string;
  authorName?: string;
}

export interface TeamMember {
  id: string;
  authUserId?: string;       // Supabase auth user ID (set after registration)
  name: string;
  email: string;
  role: TeamRole;
  title: string;             // e.g. "Stage Manager", "Props Lead"
  departments: Department[]; // which departments they work in
  avatarColor: string;       // hex color for avatar circle
  avatarUrl?: string;        // headshot image URL (signed URL from Supabase Storage)
  phone?: string;
  isActive: boolean;
  createdAt: string;
  // Check-in PIN (for kiosk path). Stored as sha256(pin + salt) server-side.
  pinHash?: string;
  pinSalt?: string;
  // HR role, deliberately separate from `role` above. An app admin can reseed
  // the database and still must not read payroll data, so `role: 'admin'`
  // grants nothing in the HR surface — hrRole is granted explicitly and
  // audited. Absent means 'none'.
  //
  // Display only: the authorization copy lives in Postgres (hr.role_grants),
  // which is what the RLS policies read. See hr-auth.tsx.
  hrRole?: HrRole;
}

// Mirrors the check constraint on hr.role_grants.hr_role.
export type HrRole = 'none' | 'manager' | 'hr-admin';

// ============================================
// CHECK-IN (presence / hours / roll-call)
// ============================================

export type CheckInMethod = 'kiosk' | 'self' | 'auto-close' | 'admin';
// Location is stored as its slug string. Seed values are below; admins
// can add/edit/archive locations at /settings/locations.
export type KnownCheckInLocation = 'office' | 'theater' | 'warehouse' | 'shop';
export type CheckInLocation = KnownCheckInLocation | (string & {});

export interface CheckIn {
  id: string;
  teamMemberId: string;
  teamMemberName: string;        // snapshot for history if member is later deleted
  avatarColor?: string;          // snapshot for presence board rendering
  location: CheckInLocation;
  checkedInAt: string;           // ISO
  checkedOutAt?: string | null;  // null = still on-site
  durationSeconds?: number;      // filled on checkout
  method: CheckInMethod;
  closeMethod?: CheckInMethod;
  note?: string;
  dayKey: string;                // YYYY-MM-DD in system timezone, set server-side at check-in
}

export interface Project {
  id: string;
  name: string;
  projectLead: string;
  dueDate?: string | null;
  kickoffDate: string;
  status: ProjectStatus;
  description: string;
  archived: boolean;
}

export interface DeptData {
  // Marketing
  marketingType?: string;
  graphicType?: string[];
  apparelMerchType?: string[];
  printType?: string[];
  socialMediaPostDay?: string;
  marketingPostCopy?: string;
  // Facility
  facilityType?: string;
  facilityTask?: string;
  // Scenic
  scenicRooms?: string[];
  // Tech
  techTypes?: string[];
  techRooms?: string[];
  // Media
  mediaType?: string;
  mediaFormats?: string[];
  // Costumes
  costumeType?: string;
  costumePieces?: string[];
  measurementStatus?: string;
  costumeRooms?: string[];
  // Props
  propType?: string;
  propRooms?: string[];
  // Website
  websiteType?: string;
  websiteSections?: string[];
  websiteUrl?: string;
}

/**
 * A linked Canva design, as StageFlow stores it.
 *
 * No thumbnail URL on purpose: Canva's expire 15 minutes after they are issued,
 * so a stored one is a broken image by the next page load. Thumbnails are
 * resolved on read through /api/canva/thumbnails.
 */
export interface CanvaDesignRef {
  id: string;
  title: string;
  /** Permanent link into Canva — unlike the thumbnail, safe to keep. */
  editUrl?: string;
}

export interface StageSMSource {
  production_id: string;
  task_type: 'production_task' | 'set_task';
  task_id: string;
  source_note_id?: string;
  source_transcript?: string;
  set_piece_id?: string;
}

export interface Deliverable {
  id: string;
  name: string;
  projectId: string;
  workingTeam: string[];
  kickoffDate: string;
  dueDate: string;
  status: DeliverableStatus;
  department: Department;
  energyLevel: EnergyLevel;
  task: string;
  type?: string;
  reviewBy?: string;
  submittedDate?: string;
  isClientWork?: boolean;
  estimatedMinutes?: number;
  isVolunteerEligible?: boolean;
  volunteerDescription?: string;
  volunteerSlotsNeeded?: number;
  volunteerSlotsFilled?: number;
  deptData?: DeptData;
  // StageSM sync metadata
  stagesm_source?: StageSMSource;
  sync_hash?: string;
  last_synced_at?: string;
  // Handoff ownership tracking
  currentOwner?: string;
  lastHandoffAt?: string;
  lastHandoffBy?: string;
  // Promotion lineage — set when this deliverable was spun off from a checklist item
  promotedFromChecklistItemId?: string;
  parentDeliverableId?: string;
  // AI plan assessment — recorded when created via Plan with AI so the
  // attempt/skip decision stays visible on the deliverable afterward.
  feasibilityLevel?: FeasibilityLevel;
  feasibilityRationale?: string;
  // Schedule/Gantt fields
  dependencies?: string[];      // deliverable ids that must finish before this can start
  progress?: number;            // 0–100 — manual override of the checklist-derived progress
  isMilestone?: boolean;        // Key Date: 0-duration marker at the due date
  milestoneLabel?: string;      // human-readable label for the Key Date (e.g. "Opening Night")
  // Linked Canva designs. Picked in DeliverableForm; previously held in local
  // state and dropped on submit, so every attachment was silently lost.
  canvaDesigns?: CanvaDesignRef[];
  // Stamped by field edits on the detail page (including rename).
  lastEditedAt?: string;
  lastEditedBy?: string;
  // Snooze/archive — hide from dashboard priorities when priorities shift.
  // snoozedUntil is a local YYYY-MM-DD; item stays hidden while today < snoozedUntil.
  // Send null to clear (undefined would be dropped by JSON.stringify and leave the server value untouched).
  snoozedUntil?: string | null;
  archived?: boolean;
  // Why/when/who for an archive. Cleared with explicit nulls on restore.
  archivedAt?: string | null;
  archivedBy?: string | null;
  archiveReason?: ArchiveReason | null;
  archiveNote?: string | null;
}

export function isSnoozed(d: Pick<Deliverable, 'snoozedUntil'>, todayStr: string): boolean {
  return !!d.snoozedUntil && d.snoozedUntil > todayStr;
}

// Format a Date as a local YYYY-MM-DD string. Avoid toISOString() here — it
// converts to UTC, so near midnight it returns the neighboring day's date.
export function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayLocalStr(): string {
  return toLocalDateStr(new Date());
}

// How logistically difficult a request is to actually pull off, from routine
// to beyond the org's realistic capability. Assigned by the AI planner and
// reviewable/overridable by the user before a deliverable is created.
export type FeasibilityLevel = 'practical' | 'possible' | 'plausible' | 'heavy-lift' | 'moonshot';

export const FEASIBILITY_LEVELS: Array<{ value: FeasibilityLevel; label: string; color: string }> = [
  { value: 'practical', label: 'Practical', color: 'emerald' },
  { value: 'possible', label: 'Possible', color: 'sky' },
  { value: 'plausible', label: 'Plausible', color: 'amber' },
  { value: 'heavy-lift', label: 'Heavy Lift', color: 'orange' },
  { value: 'moonshot', label: 'Moonshot', color: 'rose' },
];

// Child steps of a deliverable, stored in the 'deliverable-checklist' KV
// collection. kv.getByPrefix has no ORDER BY, so sortOrder (when present)
// must drive display ordering; items predating it fall back to createdAt.
export interface ChecklistItem {
  id: string;
  deliverableId: string;
  text: string;
  completed: boolean;
  createdAt: string;
  completedAt?: string;
  completedBy?: string;
  // Team member responsible for this step. Stores TeamMember.id (not the display
  // name) so the assignment survives a rename — the same reasoning CalendarEvent
  // and agenda items use for keying people by id. Resolve the id to a person for
  // display; unset/null means unassigned. Clearing must send null explicitly:
  // the KV PUT merges { ...existing, ...updates }, so an undefined field is
  // dropped by JSON.stringify and would leave the old id in place.
  assigneeId?: string | null;
  promotedToDeliverableId?: string;
  sortOrder?: number;
  estimatedMinutes?: number;
  // Actual focused time spent on this step, in seconds. Captured when the step
  // is checked off inside Focus Mode: the deliverable's pause-aware timer
  // measures the slice from when the step became active — the previous step's
  // check-off, or when Focus Mode first shows the checklist for the opening
  // step — to when it's checked off. Absent for steps completed outside Focus
  // Mode. Seconds (not minutes) to match FocusSession.durationSeconds and to
  // keep short steps meaningful.
  actualSeconds?: number;
  // Declared for future use; v1 derives step↔event links by filtering
  // CalendarEvent.checklistItemId instead of writing this back.
  scheduledEventIds?: string[];
}

/** Stable display order for checklist items: sortOrder first, then createdAt. */
export function sortChecklistItems<T extends Pick<ChecklistItem, 'sortOrder' | 'createdAt'>>(items: T[]): T[] {
  return [...items].sort((a, b) =>
    ((a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity)) || a.createdAt.localeCompare(b.createdAt)
  );
}

/**
 * Compact human label for a tracked duration in seconds: "45s", "3m 05s",
 * "1h 05m". Used wherever a checklist step's actual (focus-tracked) time is
 * shown — live or logged, Focus Mode or the deliverable detail — so they all
 * read the same. Floors rather than rounds, so it never reports more focused
 * time than was actually tracked.
 */
export function formatTrackedTime(seconds: number): string {
  const totalSec = Math.max(0, Math.floor(seconds));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
}

// Common Key Date labels theater teams recognize
export const MILESTONE_LABEL_PRESETS = [
  'Opening Night',
  'Load-in',
  'Tech Rehearsal',
  'First Preview',
  'Closing Night',
  'Strike',
  'Photo Call',
  'First Read-Through',
] as const;

export type HandoffPriority = 'low' | 'normal' | 'high' | 'urgent';
export type AcknowledgmentStatus = 'pending' | 'received' | 'accepted' | 'rejected';

export interface HandoffEvent {
  id: string;
  deliverableId: string;
  deliverableName: string;
  projectId: string;
  fromUser: string;
  toUser: string;
  toType: 'user' | 'department';
  previousStatus: DeliverableStatus;
  newStatus: DeliverableStatus;
  note: string;
  dueDate?: string;
  priority?: HandoffPriority;
  notifyRecipient: boolean;
  notificationSent: boolean;
  acknowledgmentStatus: AcknowledgmentStatus;
  createdAt: string;
}

// ============================================
// MEETING AGENDAS
// Items you want to raise the next time you sit
// down with someone — questions about a
// deliverable, decisions you need from them, or
// topics to cover. Items stay `open` until they
// are actually discussed, so anything a meeting
// doesn't get to rolls forward on its own.
// ============================================

export type AgendaItemKind = 'question' | 'decision-needed' | 'topic' | 'fyi';
export type AgendaItemStatus = 'open' | 'discussed' | 'resolved';

export interface AgendaItem {
  id: string;
  text: string;
  kind: AgendaItemKind;
  // Who this needs to be raised with. Absent means it's for the whole group —
  // a question for the room rather than one person. Use isTeamAgendaItem()
  // rather than testing this directly.
  withPersonId?: string;
  withPersonName?: string;      // snapshot, so history survives a roster change
  deliverableId?: string;       // set when the item is a question about a deliverable
  projectId?: string;
  // CalendarEvent this item is currently slotted into. Send null to take it
  // off an agenda (undefined would be dropped by JSON.stringify and leave the
  // server value untouched); the item stays on its person's queue either way.
  meetingEventId?: string | null;
  // Meetings this item was slotted into but not covered, oldest first. Appended
  // whenever it moves to a new meeting, so the UI can say "carried over".
  previousMeetingEventIds?: string[];
  status: AgendaItemStatus;
  // The answer/decision, captured when discussed. Send null to clear
  // (undefined would be dropped by JSON.stringify and leave the server value
  // untouched).
  outcome?: string | null;
  promotedToDeliverableId?: string;
  createdBy: string;
  createdAt: string;
  discussedAt?: string;
}

/** Bucket key for items raised with the group rather than one person. */
export const AGENDA_TEAM_KEY = 'team';
export const AGENDA_TEAM_LABEL = 'Whole team';

export interface AgendaAudienceGroup {
  /** Team member id, or AGENDA_TEAM_KEY for group-wide items. */
  personKey: string;
  personName: string;
  items: AgendaItem[];
  /** ISO timestamp of the longest-waiting item in the group. */
  oldestCreatedAt: string;
  /** True for the group-wide bucket — render it with a team icon, not initials. */
  isTeam: boolean;
}

export function isOpenAgendaItem(item: AgendaItem): boolean {
  return item.status === 'open';
}

/** An item for the room rather than one person. */
export function isTeamAgendaItem(item: Pick<AgendaItem, 'withPersonId'>): boolean {
  return !item.withPersonId;
}

/** Display name for whoever an item is waiting on. */
export function agendaAudienceName(item: AgendaItem): string {
  return isTeamAgendaItem(item) ? AGENDA_TEAM_LABEL : (item.withPersonName || 'Someone');
}

/** Whole days since an item was raised. 0 on the day it was created. */
export function agendaDaysWaiting(createdAt: string, now: Date = new Date()): number {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return 0;
  return Math.max(0, Math.floor((now.getTime() - created) / (1000 * 60 * 60 * 24)));
}

/**
 * Bucket open agenda items by who they're waiting on — one bucket per person,
 * plus a single group-wide bucket for items raised with the whole team. Groups
 * are ordered by their longest-waiting item so whoever you've been sitting on
 * the longest surfaces first; items within a group are oldest-first.
 */
export function groupOpenAgendaByAudience(items: AgendaItem[]): AgendaAudienceGroup[] {
  const groups = new Map<string, AgendaAudienceGroup>();

  for (const item of items) {
    if (!isOpenAgendaItem(item)) continue;
    const isTeam = isTeamAgendaItem(item);
    const personKey = isTeam ? AGENDA_TEAM_KEY : item.withPersonId!;
    const existing = groups.get(personKey);
    if (existing) {
      existing.items.push(item);
      if (item.createdAt < existing.oldestCreatedAt) existing.oldestCreatedAt = item.createdAt;
    } else {
      groups.set(personKey, {
        personKey,
        personName: agendaAudienceName(item),
        items: [item],
        oldestCreatedAt: item.createdAt,
        isTeam,
      });
    }
  }

  const result = [...groups.values()];
  for (const g of result) {
    g.items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  return result.sort((a, b) => a.oldestCreatedAt.localeCompare(b.oldestCreatedAt));
}

export interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  endDate?: string;
  startTime?: string;    // e.g. "19:00"
  endTime?: string;      // e.g. "22:00"
  notes: string;
  isPublic: boolean;
  rooms: string[];
  // TeamMember ids, not names — agenda items key on id, and an id survives a
  // roster rename. Absent on events created before attendees existed.
  attendees?: string[];
  type: 'rehearsal' | 'performance' | 'meeting' | 'workshop' | 'load-in' | 'strike' | 'work-block' | 'other';
  venueId?: string;
  projectId?: string;
  // Which Calendar this event belongs to (org-public / org-office / a member's
  // personal calendar). Optional on purpose: events created before calendars
  // existed resolve lazily via effectiveCalendarId() in calendars.ts, so no
  // data migration is required for correctness.
  calendarId?: string;
  // Auto-scheduled work blocks (type 'work-block') link back to the work they
  // advance; both optional so plain events are unaffected.
  deliverableId?: string;
  checklistItemId?: string;
  // Google Calendar sync fields
  googleCalendarEventId?: string;
  googleCalendarId?: string;
  lastSyncedAt?: string;
}

// ============================================
// CALENDAR (org / personal / shared containers)
//
// Events reference a Calendar via calendarId. Org calendars are owned by the
// organization (public = outward-facing, office = internal ops); personal
// calendars are one-per-member and (Phase 2) two-way synced with the member's
// own Google account; shared calendars (Phase 3) are shared with a subset of
// members. See calendars.ts for the pure classification/id helpers.
// ============================================
export interface Calendar {
  id: string;
  name: string;
  kind: CalendarKind;
  ownerType: 'org' | 'member';
  ownerId?: string;            // TeamMember.id when kind === 'personal' | 'shared'
  color: string;              // hex; matches TeamMember.avatarColor conventions
  visibility: 'public' | 'internal' | 'private';
  // Google routing — Phase 2 fills these in. Opaque ids only, never tokens
  // (tokens stay in the gcal:connection:* records, off the /api/all payload).
  googleConnectionId?: string;
  googleCalendarId?: string;
  // Shared-calendar subscribers — Phase 3 populates for kind === 'shared'.
  memberIds?: string[];
  createdAt: string;
  updatedAt: string;
}

// ============================================
// AGENDA ↔ MEETING (depends on CalendarEvent)
// ============================================

export function isMeetingEvent(e: CalendarEvent): boolean {
  return e.type === 'meeting';
}

/** Sort key that orders events chronologically; untimed events sort first. */
function eventSortKey(e: CalendarEvent): string {
  return `${e.date} ${e.startTime || '00:00'}`;
}

/**
 * The soonest meeting on or after `fromDateStr` that `personId` attends.
 * Returns null when nothing is scheduled — the common case, since agenda
 * items are captured long before a meeting exists.
 */
export function findNextMeetingWith(
  events: CalendarEvent[],
  personId: string,
  fromDateStr: string,
): CalendarEvent | null {
  const upcoming = events
    .filter(e => isMeetingEvent(e) && e.date >= fromDateStr)
    .filter(e => (e.attendees || []).includes(personId))
    .sort((a, b) => eventSortKey(a).localeCompare(eventSortKey(b)));
  return upcoming[0] || null;
}

/**
 * The soonest meeting on or after `fromDateStr`, whoever is attending. Used for
 * group-wide items, which can be raised at any meeting rather than waiting on
 * one person's calendar.
 */
export function findNextMeeting(events: CalendarEvent[], fromDateStr: string): CalendarEvent | null {
  const upcoming = events
    .filter(e => isMeetingEvent(e) && e.date >= fromDateStr)
    .sort((a, b) => eventSortKey(a).localeCompare(eventSortKey(b)));
  return upcoming[0] || null;
}

/** Open items already slotted into this meeting, longest-waiting first. */
export function agendaItemsForMeeting(items: AgendaItem[], eventId: string): AgendaItem[] {
  return items
    .filter(i => i.meetingEventId === eventId && isOpenAgendaItem(i))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Items covered in this meeting, most recently discussed last. */
export function agendaItemsCoveredIn(items: AgendaItem[], eventId: string): AgendaItem[] {
  return items
    .filter(i => i.meetingEventId === eventId && !isOpenAgendaItem(i))
    .sort((a, b) => (a.discussedAt || a.createdAt).localeCompare(b.discussedAt || b.createdAt));
}

/**
 * Open items worth pulling into this meeting: anything waiting on an attendee,
 * plus every group-wide item — those can be raised at any meeting, so they're
 * offered even when the event has no attendees listed. Longest-waiting first.
 */
export function suggestedAgendaItemsFor(items: AgendaItem[], event: CalendarEvent): AgendaItem[] {
  const attendees = new Set(event.attendees || []);
  return items
    .filter(i => isOpenAgendaItem(i))
    .filter(i => i.meetingEventId !== event.id)
    .filter(i => isTeamAgendaItem(i) || attendees.has(i.withPersonId!))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** How many earlier meetings this item was slotted into but not covered. */
export function agendaCarryOverCount(item: AgendaItem): number {
  return (item.previousMeetingEventIds || []).length;
}

/**
 * Patch that moves an item onto a meeting, recording the meeting it's leaving
 * so the UI can show it was carried over. Re-slotting into the same meeting is
 * a no-op rather than a bogus carry-over.
 */
export function slotIntoMeetingPatch(item: AgendaItem, eventId: string): Partial<AgendaItem> {
  if (item.meetingEventId === eventId) return {};
  const history = item.previousMeetingEventIds || [];
  return {
    meetingEventId: eventId,
    previousMeetingEventIds: item.meetingEventId
      ? [...history, item.meetingEventId]
      : history,
  };
}

export type RoomType = 'performance' | 'rehearsal' | 'shop' | 'storage' | 'dressing-room' | 'green-room' | 'lobby' | 'office' | 'classroom' | 'other';

export const ROOM_TYPES: { value: RoomType; label: string }[] = [
  { value: 'performance', label: 'Performance Space' },
  { value: 'rehearsal', label: 'Rehearsal Space' },
  { value: 'shop', label: 'Shop (Scene/Costume/Paint)' },
  { value: 'storage', label: 'Storage' },
  { value: 'dressing-room', label: 'Dressing Room' },
  { value: 'green-room', label: 'Green Room' },
  { value: 'lobby', label: 'Lobby / Front of House' },
  { value: 'office', label: 'Office / Admin' },
  { value: 'classroom', label: 'Classroom / Education' },
  { value: 'other', label: 'Other' },
];

export const ROOM_FEATURES = [
  'Fly System', 'Sprung Floor', 'Piano', 'Sound System',
  'Lighting Grid', 'Mirrors', 'Loading Dock Access',
  'ADA Accessible', 'Climate Controlled', 'WiFi',
  'Orchestra Pit', 'Wing Space', 'Trap Room',
];

export interface VenueRoom {
  id: string;
  name: string;
  type: RoomType;
  capacity?: number;
  isPerformanceSpace: boolean;
  features: string[];
  notes?: string;
}

export interface Venue {
  id: string;
  name: string;
  venueType: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  website: string;
  contactEmail: string;
  contactFirstName: string;
  contactLastName: string;
  contactPosition: string;
  currentlyUsing: boolean;
  coiExpiry: string;
  hasContract: boolean;
  colorLabel: string;
  notes: string;
  rooms: VenueRoom[];
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  company: string;
  phone: string;
  assignee: string;
  status: CustomerStatus;
  eventName: string;
  eventType: string;
  eventLocation: string;
  eventMoveInDate: string;
  eventOpeningDate: string;
  workTypes: string[];
  forPurchaseOrRent: string;
  notes: string;
  created: string;
  archived: boolean;
}

export interface ProposalEvent {
  id: string;
  title: string;
  type: string;
  specificName: string;
  startDate: string;
  endDate: string;
  venueId: string;
  notes: string;
  isPublic: boolean;
  status: ProposalStatus;
  facilityRooms: string[];
  facilityStartTime: string;
  facilityEndTime: string;
}

export interface SocialMediaPost {
  id: string;
  name: string;
  type: string;
  copy: string;
  graphic?: string;
  reelContent?: string;
  deliverableId?: string;
  postDate: string;
  isNewIdea: boolean;
  status: 'idea' | 'draft' | 'ready' | 'posted';
  created: string;
  // Marketing machine extensions (all optional for backward compat)
  platforms?: string[];          // e.g. ['Instagram', 'Facebook', 'TikTok']
  hashtags?: string;             // "#theater #community #livearts"
  projectId?: string;            // linked project/campaign
  assignee?: string;             // team member name
  category?: string;             // content category
  notes?: string;                // internal notes / strategy
  charCount?: number;            // tracked for convenience
  // Auto-posting & scheduling
  scheduledTime?: string;        // ISO datetime for auto-posting
  autoPostEnabled?: boolean;     // whether to auto-post via connected APIs
  postingStatus?: 'scheduled' | 'posting' | 'posted' | 'failed';
  postingError?: string;         // last posting error message
  // AI-generated platform-specific copy variants
  platformCopies?: Record<string, string>; // { instagram: "...", twitter: "...", ... }
  // Post image / graphic attachment
  imageUrl?: string;              // signed URL for display
  imageStoragePath?: string;      // Supabase storage path
  imageSource?: 'upload' | 'canva' | 'url'; // origin of the image
  canvaDesignId?: string;         // Canva design ID (if source === 'canva')
  canvaDesignTitle?: string;      // Canva design title
  imageThumbnailUrl?: string;     // thumbnail for card preview
  imageFileName?: string;         // original filename
}

export interface Volunteer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  datesAvailable: string[];
  age: number;
  workAbilities: string[];
  notes: string;
  opportunityId?: string;
}

export type WorkAbilityLevel = 'heavily-physical' | 'some-physical' | 'light-physical';

export interface VolunteerDateSlot {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  label: string;
  color: string;
}

export interface VolunteerOpportunity {
  id: string;
  title: string;
  projectId: string;
  description: string;
  wearInfo: string;
  bringInfo: string;
  minAge: number;
  workAbilityOptions: { value: WorkAbilityLevel; label: string; color: string }[];
  dateSlots: VolunteerDateSlot[];
  isActive: boolean;
  submissions: number;
}

export interface MerchItem {
  id: string;
  name: string;
  itemType: string;
  logoVerbiage: string;
  saying: string;
  showSpecificPun: string;
  status: 'concept' | 'design' | 'production' | 'available';
  // --- New fields ---
  category: 'show-merch' | 'company-brand' | 'event';
  projectId?: string;
  linkedDeliverableIds?: string[];
  vendor?: string;
  vendorContact?: string;
  vendorUrl?: string;
  printMethod?: string;
  unitCost?: number;
  sellPrice?: number;
  variants?: MerchVariant[];
  totalOrdered?: number;
  totalInStock?: number;
  totalSold?: number;
  designUrl?: string;
  orderDate?: string;
  expectedDelivery?: string;
  receivedDate?: string;
  notes?: string;
  imageUrl?: string;
  grabBagContents?: string[];
}

export interface MerchVariant {
  label: string;
  ordered: number;
  inStock: number;
  sold: number;
}

export type MerchCategory = 'show-merch' | 'company-brand' | 'event';

export const MERCH_ITEM_TYPES = [
  'T-Shirt', 'Sweatshirt', 'Stickers', 'Pin', 'Grab Bag',
  'Tote Bag', 'Mug', 'Hat', 'Hoodie', 'Button', 'Magnet',
  'Notebook', 'Tumbler', 'Other',
] as const;

export const MERCH_PRINT_METHODS = [
  'Screen Print', 'DTG (Direct to Garment)', 'Heat Transfer / Vinyl',
  'Embroidery', 'Sublimation', 'Die-Cut', 'Enamel', 'UV Print',
  'Laser Engrave', 'Other',
] as const;

export const APPAREL_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'] as const;

// ============================================
// STATIC CONSTANTS (UI config, not DB records)
// ============================================

export const ROOMS = [
  'Main Stage', 'Rehearsal Hall', 'Black Box', 'Green Room', 'Scene Shop',
  'Costume Shop', 'Classroom A', 'Classroom B', 'Office 1', 'Office 2',
  'Lobby', 'Loading Dock', 'Storage'
];

export const TEAM_MEMBERS = [
  'Sarah Chen', 'Marcus Williams', 'Jess Rivera', 'David Park',
  'Amara Okafor', 'Riley Thompson', 'Jordan Blake', 'Casey Morgan'
];

export const DEPARTMENTS: { value: Department; label: string; description: string; color: string; bgColor: string; dotColor: string; borderColor: string }[] = [
  { value: 'Marketing', label: 'Marketing', description: 'graphics, social etc', color: 'text-indigo-700', bgColor: 'bg-indigo-100', dotColor: 'bg-indigo-500', borderColor: 'border-indigo-200' },
  { value: 'Facility', label: 'Facility', description: 'our new spaces', color: 'text-slate-700', bgColor: 'bg-slate-200', dotColor: 'bg-slate-500', borderColor: 'border-slate-300' },
  { value: 'Scenic', label: 'Scenic', description: '', color: 'text-emerald-700', bgColor: 'bg-emerald-100', dotColor: 'bg-emerald-500', borderColor: 'border-emerald-200' },
  { value: 'Costumes', label: 'Costumes', description: '', color: 'text-amber-700', bgColor: 'bg-amber-100', dotColor: 'bg-amber-500', borderColor: 'border-amber-200' },
  { value: 'Props', label: 'Props', description: '', color: 'text-red-700', bgColor: 'bg-red-100', dotColor: 'bg-red-500', borderColor: 'border-red-200' },
  { value: 'Tech', label: 'Tech', description: '', color: 'text-slate-700', bgColor: 'bg-slate-100', dotColor: 'bg-slate-400', borderColor: 'border-slate-200' },
  { value: 'Website', label: 'Website', description: '', color: 'text-blue-700', bgColor: 'bg-blue-100', dotColor: 'bg-blue-500', borderColor: 'border-blue-200' },
  { value: 'Media', label: 'Media', description: 'film, moving graphic, audio recording, photography etc', color: 'text-purple-700', bgColor: 'bg-purple-100', dotColor: 'bg-purple-500', borderColor: 'border-purple-200' },
  { value: 'Creative', label: 'Creative', description: 'directors, writers, artistic team', color: 'text-violet-700', bgColor: 'bg-violet-100', dotColor: 'bg-violet-500', borderColor: 'border-violet-200' },
  { value: 'Stage Management', label: 'Stage Management', description: '', color: 'text-sky-700', bgColor: 'bg-sky-100', dotColor: 'bg-sky-500', borderColor: 'border-sky-200' },
  { value: 'Design', label: 'Design', description: 'scenic, lighting, sound designers', color: 'text-teal-700', bgColor: 'bg-teal-100', dotColor: 'bg-teal-500', borderColor: 'border-teal-200' },
  { value: 'Production Leadership', label: 'Production Leadership', description: '', color: 'text-orange-700', bgColor: 'bg-orange-100', dotColor: 'bg-orange-500', borderColor: 'border-orange-200' },
  { value: 'Electrics', label: 'Electrics', description: 'lighting rig & board ops', color: 'text-yellow-700', bgColor: 'bg-yellow-100', dotColor: 'bg-yellow-500', borderColor: 'border-yellow-200' },
  { value: 'Sound', label: 'Sound', description: 'audio engineers, board ops, mixers', color: 'text-cyan-700', bgColor: 'bg-cyan-100', dotColor: 'bg-cyan-500', borderColor: 'border-cyan-200' },
  { value: 'Show Control', label: 'Show Control', description: 'QLab & cue automation', color: 'text-lime-700', bgColor: 'bg-lime-100', dotColor: 'bg-lime-500', borderColor: 'border-lime-200' },
  { value: 'Video', label: 'Video', description: 'projection & video playback', color: 'text-fuchsia-700', bgColor: 'bg-fuchsia-100', dotColor: 'bg-fuchsia-500', borderColor: 'border-fuchsia-200' },
  { value: 'Rigging', label: 'Rigging', description: 'deck & flies', color: 'text-stone-700', bgColor: 'bg-stone-200', dotColor: 'bg-stone-500', borderColor: 'border-stone-300' },
  { value: 'Special Effects', label: 'Special Effects', description: 'pyro, fog, atmospherics', color: 'text-rose-700', bgColor: 'bg-rose-100', dotColor: 'bg-rose-500', borderColor: 'border-rose-200' },
  { value: 'Wardrobe', label: 'Wardrobe', description: 'run-crew, quick changes, laundry', color: 'text-pink-700', bgColor: 'bg-pink-100', dotColor: 'bg-pink-500', borderColor: 'border-pink-200' },
  { value: 'Hair & Makeup', label: 'Hair & Makeup', description: '', color: 'text-rose-700', bgColor: 'bg-rose-200', dotColor: 'bg-rose-600', borderColor: 'border-rose-300' },
  { value: 'Music', label: 'Music', description: 'MD, pit, accompanist', color: 'text-green-700', bgColor: 'bg-green-100', dotColor: 'bg-green-500', borderColor: 'border-green-200' },
  { value: 'Production Support', label: 'Production Support', description: 'general production crew', color: 'text-zinc-700', bgColor: 'bg-zinc-100', dotColor: 'bg-zinc-500', borderColor: 'border-zinc-200' },
  { value: 'Education', label: 'Education', description: 'teaching artists, classes', color: 'text-orange-700', bgColor: 'bg-orange-200', dotColor: 'bg-orange-600', borderColor: 'border-orange-300' },
  { value: 'Volunteer', label: 'Volunteer', description: 'community & volunteer teams', color: 'text-neutral-700', bgColor: 'bg-neutral-200', dotColor: 'bg-neutral-500', borderColor: 'border-neutral-300' },
];

// ============================================
// EDITABLE DEPARTMENTS / LOCATIONS / AUDIT LOG
// Managed at /settings/departments, /settings/locations, /settings/audit-log.
// DEPARTMENTS above is preserved as the seed source and static fallback;
// live records live in Supabase and are exposed via useDepartments().
// ============================================

// Tailwind color families supported by the department palette. Each maps
// to a full color-token bundle used by pickers, chips, and badges.
export type DepartmentColorFamily =
  | 'slate' | 'gray' | 'zinc' | 'stone' | 'neutral'
  | 'red' | 'orange' | 'amber' | 'yellow' | 'lime'
  | 'green' | 'emerald' | 'teal' | 'cyan' | 'sky'
  | 'blue' | 'indigo' | 'violet' | 'purple' | 'fuchsia'
  | 'pink' | 'rose';

export interface DepartmentRecord {
  id: string;                     // slug — matches TeamMember.departments entries
  name: string;                   // display label
  description: string;
  colorFamily: DepartmentColorFamily;
  headTeamMemberId?: string | null;
  sortOrder: number;
  isArchived: boolean;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LocationRecord {
  id: string;                     // slug — matches CheckIn.location values
  name: string;
  description?: string;
  sortOrder: number;
  isArchived: boolean;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AuditEntity = 'department' | 'location' | 'team-member' | 'deliverable' | 'settings' | 'other';
export type AuditAction = 'create' | 'update' | 'archive' | 'restore' | 'delete';

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  actorId?: string | null;
  actorName: string;
  entity: AuditEntity;
  entityId: string;
  entityLabel: string;
  action: AuditAction;
  summary: string;
  // Optional per-field before/after snapshot for update actions.
  changes?: Array<{ field: string; before: unknown; after: unknown }>;
}

// Map the static DEPARTMENTS palette above to color-family slugs the editor
// exposes. Used to seed the departments table and back-fill existing rows.
const DEPARTMENT_COLOR_FAMILY_MAP: Record<KnownDepartment, DepartmentColorFamily> = {
  Marketing: 'indigo', Facility: 'slate', Scenic: 'emerald', Costumes: 'amber',
  Props: 'red', Tech: 'slate', Website: 'blue', Media: 'purple',
  Creative: 'violet', 'Stage Management': 'sky', Design: 'teal',
  'Production Leadership': 'orange', Electrics: 'yellow', Sound: 'cyan',
  'Show Control': 'lime', Video: 'fuchsia', Rigging: 'stone',
  'Special Effects': 'rose', Wardrobe: 'pink', 'Hair & Makeup': 'rose',
  Music: 'green', 'Production Support': 'zinc', Education: 'orange',
  Volunteer: 'neutral',
};

// Seed used the first time the departments table is empty.
export const DEFAULT_DEPARTMENTS: Omit<DepartmentRecord, 'createdAt' | 'updatedAt'>[] =
  DEPARTMENTS.map((d, idx) => ({
    id: d.value,
    name: d.label,
    description: d.description,
    colorFamily: DEPARTMENT_COLOR_FAMILY_MAP[d.value as KnownDepartment] ?? 'slate',
    headTeamMemberId: null,
    sortOrder: idx,
    isArchived: false,
    archivedAt: null,
  }));

// Seed used the first time the locations table is empty. Matches the
// original CheckInLocation union.
export const DEFAULT_LOCATIONS: Omit<LocationRecord, 'createdAt' | 'updatedAt'>[] = [
  { id: 'office',    name: 'Office',    description: 'Main office',    sortOrder: 0, isArchived: false, archivedAt: null },
  { id: 'theater',   name: 'Theater',   description: 'Performance venue', sortOrder: 1, isArchived: false, archivedAt: null },
  { id: 'warehouse', name: 'Warehouse', description: 'Storage & load-in', sortOrder: 2, isArchived: false, archivedAt: null },
  { id: 'shop',      name: 'Shop',      description: 'Build shop',     sortOrder: 3, isArchived: false, archivedAt: null },
];

// Full Tailwind class bundle for a given department color family. Kept in
// sync with the palette used in the static DEPARTMENTS array so admin-added
// departments render identically to the seed set.
export function colorTokensForFamily(family: DepartmentColorFamily): {
  color: string; bgColor: string; dotColor: string; borderColor: string;
} {
  const shadeMap: Record<DepartmentColorFamily, { c: string; bg: string; dot: string; border: string }> = {
    slate:   { c: '700', bg: '100', dot: '500', border: '200' },
    gray:    { c: '700', bg: '100', dot: '500', border: '200' },
    zinc:    { c: '700', bg: '100', dot: '500', border: '200' },
    stone:   { c: '700', bg: '200', dot: '500', border: '300' },
    neutral: { c: '700', bg: '200', dot: '500', border: '300' },
    red:     { c: '700', bg: '100', dot: '500', border: '200' },
    orange:  { c: '700', bg: '100', dot: '500', border: '200' },
    amber:   { c: '700', bg: '100', dot: '500', border: '200' },
    yellow:  { c: '700', bg: '100', dot: '500', border: '200' },
    lime:    { c: '700', bg: '100', dot: '500', border: '200' },
    green:   { c: '700', bg: '100', dot: '500', border: '200' },
    emerald: { c: '700', bg: '100', dot: '500', border: '200' },
    teal:    { c: '700', bg: '100', dot: '500', border: '200' },
    cyan:    { c: '700', bg: '100', dot: '500', border: '200' },
    sky:     { c: '700', bg: '100', dot: '500', border: '200' },
    blue:    { c: '700', bg: '100', dot: '500', border: '200' },
    indigo:  { c: '700', bg: '100', dot: '500', border: '200' },
    violet:  { c: '700', bg: '100', dot: '500', border: '200' },
    purple:  { c: '700', bg: '100', dot: '500', border: '200' },
    fuchsia: { c: '700', bg: '100', dot: '500', border: '200' },
    pink:    { c: '700', bg: '100', dot: '500', border: '200' },
    rose:    { c: '700', bg: '100', dot: '500', border: '200' },
  };
  const s = shadeMap[family];
  return {
    color: `text-${family}-${s.c}`,
    bgColor: `bg-${family}-${s.bg}`,
    dotColor: `bg-${family}-${s.dot}`,
    borderColor: `border-${family}-${s.border}`,
  };
}

// ============================================
// "DO THIS NEXT" ENGINE (pure computation)
// ============================================

export interface PrioritizedTask {
  deliverable: Deliverable;
  project: Project;
  score: number;
  urgencyLabel: 'overdue' | 'today' | 'urgent' | 'soon' | 'planned';
  reason: string;
  daysRemaining: number;
}

export function calculatePriority(deliverables: Deliverable[], projects: Project[]): PrioritizedTask[] {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const todayStr = toLocalDateStr(now);

  return deliverables
    .filter(d => d.status !== 'complete' && d.status !== 'approved')
    .filter(d => !d.archived && !isSnoozed(d, todayStr))
    .map(d => {
      const project = projects.find(p => p.id === d.projectId)!;
      if (!project || INACTIVE_PROJECT_STATUSES.has(project.status)) return null;
      const due = new Date(d.dueDate);
      due.setHours(0, 0, 0, 0);
      const daysRemaining = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      let score = 0;
      let urgencyLabel: PrioritizedTask['urgencyLabel'] = 'planned';
      let reason = '';

      if (daysRemaining < 0) {
        score = 100 + Math.abs(daysRemaining) * 10;
        urgencyLabel = 'overdue';
        reason = `⚠️ ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) > 1 ? 's' : ''} overdue!`;
      } else if (daysRemaining === 0) {
        score = 95;
        urgencyLabel = 'today';
        reason = '📍 Due today!';
      } else if (daysRemaining <= 3) {
        score = 80 + (3 - daysRemaining) * 5;
        urgencyLabel = 'urgent';
        reason = `🔥 Due in ${daysRemaining} day${daysRemaining > 1 ? 's' : ''}`;
      } else if (daysRemaining <= 7) {
        score = 50 + (7 - daysRemaining) * 5;
        urgencyLabel = 'soon';
        reason = `⏰ Due in ${daysRemaining} days`;
      } else {
        score = Math.max(10, 40 - daysRemaining);
        urgencyLabel = 'planned';
        reason = `📅 Due in ${daysRemaining} days`;
      }

      if (d.status === 'not-started') score *= 1.3;
      if (d.status === 'review') score *= 0.7;
      if (d.isClientWork) score *= 1.5;

      return { deliverable: d, project, score, urgencyLabel, reason, daysRemaining };
    })
    .filter(Boolean)
    // Scores bottom out at 10, so everything due a month or more out ties.
    // Among equals the one due sooner goes first, rather than whichever the
    // store returned first (the TV queue lists all of them).
    .sort((a, b) => b!.score - a!.score || a!.daysRemaining - b!.daysRemaining) as PrioritizedTask[];
}

export function getProjectCompletion(projectId: string, deliverables: Deliverable[]): { completed: number; total: number; percentage: number } {
  return summarizeCompletion(deliverables.filter(d => d.projectId === projectId));
}

export function getTimelinePosition(kickoff: string, due?: string | null): number {
  if (!due) return -1; // no timeline for ongoing projects
  const now = new Date().getTime();
  const start = new Date(kickoff).getTime();
  const end = new Date(due).getTime();
  if (now <= start) return 0;
  if (now >= end) return 100;
  return Math.round(((now - start) / (end - start)) * 100);
}